import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  IssueState,
  type DashboardLoopDto,
  type RecentActivityGroupDto,
} from '@aiai/shared';
import { fetchMyOverview } from '../api';
import { qk } from '../api/queryKeys';
import { Badge } from '../components/Badge';
import { Row, RowList } from '../components/Row';
import { LoopStateBadge, StatusBadge } from '../components/StatusBadge';
import { StopAllButton } from '../components/StopAllButton';
import { NewPrototypeModal } from '../components/NewPrototypeModal';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useOrg } from '../org/useOrg';
import { useCapabilities } from '../hooks/useCapabilities';
import { formatRelative } from '../lib/format';
import { loopEditHref, newIssueHref, projectHref } from '../lib/projectNav';
import { workspacePath } from '../lib/workspacePath';
import { SectionRuleHeader } from '../components/SectionRuleHeader';
import { ProjectPickerModal } from '../components/ProjectPickerModal';
import { NowCard } from '../components/NowCard';
import { isWorkingRun } from '../hub/layout';

function greeting(): string {
  const h = new Date().getHours();
  if (h < 6) return 'Up late';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * One loop row in the Automations section. On the cross-workspace Overview these
 * are monitor + drill-in: status, name, schedule, linking into the loop's own
 * workspace where the fire/enable controls live (firing is a workspace-scoped
 * mutation, so it happens in-workspace rather than from this global view).
 */
function AutomationRow({ loop }: { loop: DashboardLoopDto }) {
  const editHref = workspacePath(
    loop.workspace.slug,
    loopEditHref(loop.projectKind, loop.projectCode ?? loop.projectId, loop.id),
  );
  const timestamp = loop.nextRunAt
    ? `Next ${formatRelative(loop.nextRunAt)}`
    : loop.lastFiredAt
      ? `Last fired ${formatRelative(loop.lastFiredAt)}`
      : undefined;

  return (
    <Row
      to={editHref}
      badge={<LoopStateBadge enabled={loop.enabled} size="sm" />}
      title={loop.name}
      timestamp={timestamp}
    />
  );
}

/** Enabled first, then soonest next fire (unscheduled last), then name. */
function loopSort(a: DashboardLoopDto, b: DashboardLoopDto): number {
  if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
  const an = a.nextRunAt ? new Date(a.nextRunAt).getTime() : Infinity;
  const bn = b.nextRunAt ? new Date(b.nextRunAt).getTime() : Infinity;
  if (an !== bn) return an - bn;
  return a.name.localeCompare(b.name);
}

interface LoopGroup {
  projectId: string;
  projectName: string;
  projectKind: DashboardLoopDto['projectKind'];
  workspaceName: string;
  workspaceSlug: string;
  loops: DashboardLoopDto[];
}

/**
 * "Recent activity" — the last few projects/prototypes touched across every
 * workspace (newest group first), each with its most-recent issues. The server
 * already grouped, ordered, and capped these; each group header links to its
 * project/prototype page. Hidden when there's nothing recent.
 */
function RecentSection({ groups }: { groups: RecentActivityGroupDto[] }) {
  if (groups.length === 0) return null;

  return (
    <section>
      <SectionRuleHeader
        label="Recent activity"
        right={`${groups.length} project${groups.length === 1 ? '' : 's'}`}
      />
      <div className="space-y-4">
        {groups.map((group) => (
          <div key={group.projectId}>
            <Link
              to={workspacePath(
                group.workspaceSlug,
                projectHref(group.projectKind, group.projectCode ?? group.projectId),
              )}
              className="mb-1 flex min-w-0 items-center gap-2 px-1 hover:underline"
            >
              {/* The name is what you scan for — it wins the row; the
                  workspace label truncates first on narrow screens. */}
              <span className="min-w-0 truncate text-sm font-medium text-ink">{group.projectName}</span>
              <span className="min-w-0 max-w-[6rem] truncate font-mono text-2xs text-ink-faint sm:max-w-none">
                {group.workspaceName}
              </span>
              <Badge tone="neutral" size="sm" dot={false}>
                {group.projectKind === 'prototype' ? 'Prototype' : 'Repo'}
              </Badge>
            </Link>
            <RowList>
              {group.issues.map((issue) => (
                <Row
                  key={issue.id}
                  to={workspacePath(group.workspaceSlug, `/issues/${issue.code ?? issue.id}`)}
                  badge={<StatusBadge status={issue.state} resolution={issue.resolution} size="sm" />}
                  title={
                    <>
                      {issue.code && (
                        <span className="mr-1.5 font-mono text-[0.7rem] uppercase tracking-wide text-ink-faint">
                          {issue.code}
                        </span>
                      )}
                      {issue.title}
                    </>
                  }
                  timestamp={formatRelative(issue.updatedAt)}
                />
              ))}
            </RowList>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * "Automations" — every scheduled loop across all workspaces, grouped by its
 * project/prototype (each group tagged with its workspace) so the home screen
 * reads as a per-project overview of the whole system. Hidden entirely when the
 * user has no loops.
 */
function AutomationsSection({
  loops,
  openCountByProject,
  idleNudge,
}: {
  loops: DashboardLoopDto[];
  openCountByProject: Map<string, number>;
  idleNudge?: boolean;
}) {
  const groups = useMemo<LoopGroup[]>(() => {
    const byProject = new Map<string, LoopGroup>();
    for (const loop of loops) {
      let group = byProject.get(loop.projectId);
      if (!group) {
        group = {
          projectId: loop.projectId,
          projectName: loop.projectName,
          projectKind: loop.projectKind,
          workspaceName: loop.workspace.name,
          workspaceSlug: loop.workspace.slug,
          loops: [],
        };
        byProject.set(loop.projectId, group);
      }
      group.loops.push(loop);
    }
    for (const group of byProject.values()) group.loops.sort(loopSort);
    // Groups: those with an enabled loop first, then most open issues, then name.
    return [...byProject.values()].sort((a, b) => {
      const aOn = a.loops.some((l) => l.enabled);
      const bOn = b.loops.some((l) => l.enabled);
      if (aOn !== bOn) return aOn ? -1 : 1;
      const ao = openCountByProject.get(a.projectId) ?? 0;
      const bo = openCountByProject.get(b.projectId) ?? 0;
      if (ao !== bo) return bo - ao;
      return a.projectName.localeCompare(b.projectName);
    });
  }, [loops, openCountByProject]);

  if (loops.length === 0) return null;

  const enabledCount = loops.filter((l) => l.enabled).length;

  return (
    <section>
      <SectionRuleHeader
        label="Automations"
        right={`${enabledCount}/${loops.length} on · ${groups.length} project${groups.length === 1 ? '' : 's'}`}
      />
      {idleNudge && enabledCount === 0 && (
        <p className="mb-3 text-xs text-ink-muted">
          Re-enable a loop to resume automated runs.
        </p>
      )}
      <div className="space-y-4">
        {groups.map((group) => {
          const openCount = openCountByProject.get(group.projectId) ?? 0;
          const onCount = group.loops.filter((l) => l.enabled).length;
          return (
            <div key={group.projectId}>
              <div className="mb-1 flex items-center justify-between gap-2 px-1">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {/* Name wins the row; the workspace label truncates first. */}
                  <span className="min-w-0 truncate text-sm font-medium text-ink">{group.projectName}</span>
                  <span className="min-w-0 max-w-[6rem] truncate font-mono text-2xs text-ink-faint sm:max-w-none">
                    {group.workspaceName}
                  </span>
                  <Badge tone="neutral" size="sm" dot={false}>
                    {group.projectKind === 'prototype' ? 'Prototype' : 'Repo'}
                  </Badge>
                </div>
                <span className="shrink-0 text-xs tabular-nums text-ink-muted">
                  {openCount} open · {onCount}/{group.loops.length} on
                </span>
              </div>
              <RowList>
                {group.loops.map((loop) => (
                  <AutomationRow key={loop.id} loop={loop} />
                ))}
              </RowList>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function ActivityPage() {
  useDocumentTitle('Activity');
  const navigate = useNavigate();
  const { orgs } = useOrg();
  const { can, canInAny, workspacesWith } = useCapabilities();
  const [picker, setPicker] = useState<'issue' | 'loop' | null>(null);
  const [showPrototypeModal, setShowPrototypeModal] = useState(false);

  const overviewQuery = useQuery({
    queryKey: qk.myOverview,
    queryFn: fetchMyOverview,
    refetchInterval: 10_000,
  });

  const overview = overviewQuery.data;
  const active = useMemo(() => overview?.activeRuns ?? [], [overview]);
  const allIssues = useMemo(() => overview?.issues ?? [], [overview]);
  const loops = useMemo(() => overview?.loops ?? [], [overview]);
  const projects = useMemo(() => overview?.projects ?? [], [overview]);
  const recentActivity = useMemo(() => overview?.recentActivity ?? [], [overview]);

  // Live agents currently working (exclude those paused awaiting a gate).
  const nowRuns = useMemo(() => active.filter((r) => !r.awaitingGate), [active]);

  // projectId → name, for labelling issue/run cards with their project.
  const projectNameById = useMemo(
    () => new Map(projects.map((p) => [p.id, p.name])),
    [projects],
  );

  const openIssueCount = useMemo(
    () => allIssues.filter((i) => i.state !== IssueState.Closed).length,
    [allIssues],
  );

  // projectId → open-issue count, reused to badge each Automations group.
  const openCountByProject = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of projects) counts.set(p.id, p.openIssueCount);
    return counts;
  }, [projects]);

  // A user who belongs to no workspace gets the dedicated ask-for-an-invite
  // screen, not an empty dashboard.
  if (overviewQuery.isFetched && orgs.length === 0) {
    return <Navigate to="/no-workspace" replace />;
  }

  // Owner-only create actions are keyed on "may create in at least one
  // workspace"; the create modal's own workspace selector handles the target.
  const canNewPrototype = canInAny('prototype:manage');
  const canNewLoop = canInAny('loop:manage');
  const loopWorkspaceIds = new Set(workspacesWith('loop:manage').map((o) => o.id));
  const loopProjects = projects.filter((p) => loopWorkspaceIds.has(p.workspace.id));
  // Only offer "New issue" for projects the caller may actually create issues in
  // (owner, or a contributor grant) — a viewer-granted project is read-only.
  const issueProjects = projects.filter((p) => can('issue:create', { projectId: p.id }));

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight text-ink">{greeting()}</h1>
        <p className="mt-1 text-sm text-ink-muted">
          {openIssueCount > 0
            ? `${openIssueCount} open issue${openIssueCount === 1 ? '' : 's'} across your workspaces`
            : 'No open issues'}
        </p>
      </header>

      {/* Start — new issue / prototype / loop. */}
      <section>
        <SectionRuleHeader label="Start" />
        <div className="flex flex-wrap items-center gap-2">
          {issueProjects.length > 0 && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setPicker('issue')}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              New issue
            </button>
          )}
          {canNewPrototype && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowPrototypeModal(true)}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              New prototype
            </button>
          )}
          {canNewLoop && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPicker('loop')}
              disabled={loopProjects.length === 0}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              New loop
            </button>
          )}
        </div>
      </section>

      {/* Now — live agents currently working; always shown, with an idle empty state. */}
      <section>
        <SectionRuleHeader
          label="Now"
          right={
            nowRuns.length > 0 ? (
              <span className="flex items-center gap-3">
                <span className="tabular-nums">{nowRuns.length}</span>
                {/* Pending runs are not killable yet; count working runs only. */}
                <StopAllButton stoppable={active.filter(isWorkingRun).length} />
              </span>
            ) : undefined
          }
        />
        {nowRuns.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {nowRuns.map((run) => (
              <NowCard
                key={run.id}
                run={run}
                projectName={projectNameById.get(run.projectId) ?? run.repoFullName}
              />
            ))}
          </div>
        ) : (
          <p className="rounded-lg border border-line bg-surface-2 px-4 py-6 text-center text-sm text-ink-muted">
            No agents working right now.
          </p>
        )}
      </section>

      {/* Recent activity — last 10 recently updated issues. */}
      <RecentSection groups={recentActivity} />

      {/* Automations — scheduled loops grouped by project/prototype. */}
      <AutomationsSection
        loops={loops}
        openCountByProject={openCountByProject}
        idleNudge={nowRuns.length === 0 && recentActivity.length === 0}
      />

      <ProjectPickerModal
        title={picker === 'loop' ? 'New loop in…' : 'New issue in…'}
        projects={picker === 'loop' ? loopProjects : issueProjects}
        open={picker !== null}
        onClose={() => setPicker(null)}
        hrefFor={(p) =>
          picker === 'loop'
            ? workspacePath(p.workspace.slug, `/projects/${p.code ?? p.id}/loops/new`)
            : workspacePath(p.workspace.slug, newIssueHref(p.kind, p.code ?? p.id))
        }
      />

      <NewPrototypeModal
        open={showPrototypeModal}
        onClose={() => setShowPrototypeModal(false)}
        onCreated={(p, workspaceSlug) =>
          navigate(workspacePath(workspaceSlug, `/prototypes/${p.code ?? p.id}`))
        }
      />
    </div>
  );
}
